import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { useAuthStore } from '@/stores/auth-store';
import { useBranding } from '@/features/branding/BrandingProvider';
import { isWhiteLabelBuild } from '@/lib/app-variant';

const BASE_TITLE = 'OptiSigns';

/**
 * Route-to-title mapping matching scio1 page titles.
 * Longest keys first so /app/s/personal-profile matches before shorter prefixes.
 */
const PAGE_TITLES: [string, string][] = [
  // Settings pages (/app/s/*)
  ['s/personal-profile', 'Personal Profile'],
  ['s/company-profile', 'Company Settings'],
  ['s/account-members', 'Account Members'],
  ['s/subscription-plan', 'Subscription Plan'],
  ['s/payment-history', 'Payment History'],
  ['s/payment', 'Payment'],
  ['s/order-device', 'Order Devices'],
  ['s/ordered-device', 'Order History'],
  ['s/credentials', 'Credentials'],
  ['s/apikeys', 'API Keys'],
  ['s/preference-settings', 'Preference'],
  ['s/operational-schedule', 'Operational Schedule'],
  ['s/approval-list', 'Approval List'],
  ['s/sub-account-billing', 'Sub-Account Billing'],
  ['s/coupons', 'Coupons'],
  ['s/proof-of-play-settings', 'Proof of Play Settings'],
  ['s/branding-settings', 'Branding'],
  ['s/monitoring-alert-rules', 'Monitoring Alert Rules'],
  ['s/saml-settings', 'SAML'],
  ['s/integrations', 'Integrations'],
  ['s/external-coms', 'External Communications (RS232)'],
  ['s/share-links-admin', 'Share Links Admin'],
  ['s/datasources-admin', 'DataSources'],
  ['s/feeds-admin', 'Feeds Admin'],
  ['s/social', 'Social'],
  ['s/functional-locations', 'Functional Locations'],

  // Main content pages
  ['screenManagement', 'Screens Management'],
  ['assetManagement', 'Assets Management'],
  ['playlistManagement', 'Playlists Management'],
  ['scheduleManagement', 'Schedules Management'],
  ['templateManagement', 'Templates Management'],
  ['designManagement', 'Designer'],
  ['engageManagement', 'Engage Management'],
  ['crm-management', 'Engage Hub'],
  ['feeds', 'Feeds'],

  // Analytics
  ['playbackReport', 'Proof of Play'],
  ['ai-analytics', 'AI Analytics'],
  ['security-audit-logging', 'Security Audit Logging'],

  // MDM
  ['mdm/templates', 'Provisioning Templates'],
  ['mdm/wifi-profiles', 'Wi-Fi & 802.1X'],
];

/**
 * Sets document.title based on current route, matching scio1 convention:
 * "OptiSigns – Screens Management", etc.
 * Falls back to "OptiSigns" for unmapped routes.
 */
export function usePageTitle() {
  const { pathname } = useLocation();
  // White-label: use the account's site title as the base instead of "OptiSigns".
  // POST-login source — the auth store (company.brandingData), populated after sign-in.
  const isWhiteLabel = useAuthStore((s) => !!s.company?.brandingData?.isWhiteLabel);
  const siteTitle = useAuthStore((s) => s.company?.brandingData?.siteTitle);
  // PRE-login source — on Sign In / Sign Up / Reset the auth store has no company yet, so fall back
  // to the host-resolved branding from getDomainAuth (BrandingProvider). Without this, AppShell (which
  // also wraps the auth pages for viewport control) would clobber the branded tab title that
  // useBrandingTheme set, showing "OptiSigns" on white-label login pages. `branding` is null on the
  // normal build → no effect there.
  const { branding, loading } = useBranding();
  const baseTitle = (isWhiteLabel && siteTitle) || branding?.siteTitle || BASE_TITLE;

  useEffect(() => {
    // White-label build: while getDomainAuth is still in flight (branding not yet known), DON'T write
    // the "OptiSigns" fallback — that flashes OptiSigns in the tab, then swaps to the brand once the
    // API returns. Wait until the host branding resolves (or the query settles) before setting the
    // title. On the normal build `isWhiteLabelBuild()` is false → immediate title as before.
    if (isWhiteLabelBuild() && loading && !branding) return;
    const appPath = pathname.replace('/app/', '');
    const match = PAGE_TITLES.find(([key]) => appPath.startsWith(key));
    document.title = match ? `${baseTitle} – ${match[1]}` : baseTitle;
  }, [pathname, baseTitle, loading, branding]);
}
